import {
    FormControl,
    FormGroup,
    FormGroupDirective,
    NgForm
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import {
    faCamera,
    faDiagramProject,
    faImage,
    faVideoCamera,
    IconDefinition
} from '@fortawesome/free-solid-svg-icons';

export enum Cameras {
    Matrix4G = 1
}

export enum RenderType {
    TextBox = 1,
    Checkbox = 2,
    Dropdown = 3,
    Radio = 4,
    Toggle = 5,
    Slider = 6
}
export enum InputType {
    Text = 1,
    Number = 2,
    Boolean = 3,
    Password = 4
}
export enum WifiMode {
    AccessPoint = 0,
    Station = 1,
    WifiDirect = 2,
    StationEnterPrise = 3
}

export enum PhotoSizeEnum {
    PHOTO_SIZE_42M = 0,
    PHOTO_SIZE_28M = 1,
    PHOTO_SIZE_21M = 2,
    PHOTO_SIZE_16M = 3,
    PHOTO_SIZE_12M = 4,
    PHOTO_SIZE_8M = 5,
    PHOTO_SIZE_5M = 6,
    PHOTO_SIZE_4M = 7,
    PHOTO_SIZE_3M = 8,
    PHOTO_SIZE_30M = 9,
    PHOTO_SIZE_40M = 10,
    PHOTO_SIZE_32M = 11
}

export interface GenericCameraSettingsChild extends GenericCameraSettings {
    parentValues: any;
}

export interface GenericCameraSettingsGroup {
    TabId: number;
    TabTitle: string;
    Settings: GenericCameraSettings[];
}

export class GenericCameraSettingsFunctions {
    public static toFormGroup(questions: GenericCameraSettings[]) {
        const group: any = {};
        questions.forEach((question) =>
            this.ConvertToFormGroup(group, question)
        );
        return new FormGroup(group);
    }
    public static ConvertToFormGroup(
        group: any,
        question: GenericCameraSettings
    ) {
        group[question.propertyName] = new FormControl(
            question.value || '',
            question.validations
        );
    }
}

export enum TabEnum {
    Connectivity = 1,
    Camera = 2,
    Video = 3,
    Photo = 4
}

export enum GroupEnum {
    Wi_Fi_Global_Settings = 1,
    Network_Configuration = 2,
    Bluetooth = 3,
    Four_G = 4,
    Other_Settings = 5,
    Notifications = 6,
    Screen_and_Keys = 7,
    Low_light_recording = 8,
    GPS = 9,
    Text_Overlay = 10,
    Video_Format = 11,
    Recording_Settings = 12,
    Streaming_Settings = 13,
    Pre_and_Post_Recording = 14,
    Other_Settings_2 = 15,
    Image_Quality = 16,
    Image_Capture = 17
}

export enum CameraTypes {
    Matrix = 1,
    Matrix2 = 2,
    Matrix2Plus = 3,
    Matrix4G = 4
}

export enum ApiMode {
    User = 1,
    Role = 2,
    Company = 3
}

export enum CameraType {
    Matrix = 1,
    Matrix2 = 2,
    Matrix2Plus = 3,
    Matrix4G = 4
}

export class ExternalCameraSetting {
    updateMode: ApiMode;
    cameraType: CameraType;
    updateID: number;
    updatePayload: string;
}

export class PropertySideEffects {
    givenValues: (string | number | Date | boolean)[];
    propertyNames: string[];
    value?: string | number | Date | boolean;
    disabled?: boolean;
    hidden?: boolean;
}

export interface GenericCameraSettings {
    groupId: GroupEnum;
    orderindex: number;
    propertyName: string;
    displayName: string;
    render: boolean;
    disabled?: boolean;
    inputType: InputType;
    value: string | number | Date | boolean;
    defaultValue: string | number | Date | boolean;
    renderType: RenderType;
    size: number;
    renderTypeValues?: dropdownValues[];
    validations?: any;
    validationErrorMessages?: any;
    effects?: PropertySideEffects[];
    step?: number;
    min?: number;
    max?: number;
}
export interface dropdownValues {
    value: number;
    text: string;
}

export class CameraSettingsErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(
        control: FormControl | null,
        form: FormGroupDirective | NgForm | null
    ): boolean {
        const isSubmitted = form && form.submitted;
        return !!(
            control &&
            control.invalid &&
            (control.dirty || control.touched || isSubmitted)
        );
    }
}
export class Camera {
    id: number;
    name: string;
}

export class TabWithIcon {
    id: number;
    icon: IconDefinition;
    title: string;
}

export class Group {
    tabId: number;
    id: GroupEnum;
    title: string;
}

export class CameraSettingTab {
    tab: TabWithIcon;
    groups: CameraSettingGroup[];
}
export class CameraSettingGroup {
    group: Group;
    isOpen: boolean = true;
    initialValues?: GenericCameraSettings[];
    formProperties?: GenericCameraSettings[];
}

export class ErrorMessages {
    static NetworkName_Pattern: string =
        'Invalid network name. Up to 32 printable characters are allowed.';
    static ServerIP_Pattern: string =
        'Invalid server IP. Please provide a valid IP Address.';
    static Ssid_required: string = 'Wi-Fi SSID is required';
    static Ssid_Pattern: string =
        'Invalid hotspot name. Up to 32 printable characters are allowed.';
    static APassword_Pattern: string =
        'Invalid password. Minimum 8 to maximum 32 printable characters are allowed.';
    static STPassword_Pattern: string =
        'Invalid password. Minimum 8 to maximum 32 printable characters are allowed.';
    static APassword_Required: string = 'Password is required';
    static STPassword_Required: string = 'Password is required';
    static APSsid_Pattern: string = 'Invalid hotspot name';
    static P2PPinCode_Pattern: string = 'Only 8 digits are allowed';
    static Bluetooth_PairMac_Pattern: string =
        'Mac address should be in proper format e.g. C0:84:7D:78:A9:74';
    static FourG_SIMPIN_Pattern: string =
        'Pin code should be exactly of 8 digits';
    static GPSFrequency_Pattern: string =
        'GPS frequency may only contain digits';
    static GPSFrequency_Min: string =
        'GPS frequency value may not be less than 1';
    static GPSFrequency_Max: string =
        'GPS frequency value may not exceed 32767';
    static DeviceName_Pattern: string =
        'Invalid device name. Up to 32 printable characters are allowed.';
    static DeviceName_Required: string = 'Device name is required.';
}

export class PropertyNames {
    static WifiConnectionMode: string[] = [
        'WifiConnectionMode',
        'Wi-Fi mode'
    ];
    static WifiMode: string[] = ['WifiMode', 'Wi-Fi'];
    static WIFI_ConnectTimeout: string[] = [
        'WIFI_ConnectTimeout',
        'Wi-Fi connection timeout'
    ];
    // static DataEncryption: string[] = ['DataEncryption', 'Data encryption'];
    static Ssid: string[] = ['Ssid', 'Wi-Fi SSID'];
    static APPassword: string[] = ['APPassword', 'Wi-Fi password'];
    static STPassword: string[] = ['Password', 'Wi-Fi password'];
    static APCameraIP: string[] = ['APCameraIP', 'Assign camera IP'];
    static STA_AutoGetIP: string[] = ['STA_AutoGetIP', 'Use fixed IP'];
    static UpdateCertificate: string[] = [
        'UpdateCertificate',
        'Update enterprise certificate'
    ];
    // static FourG_SIMPIN: string[] = ['FourG_SIMPIN', 'Wi-Fi direct PIN'];
    static Four_G: string[] = ['G4Mode', '4G'];
    static MediaServerAddress: string[] = [
        'MediaServerAddress',
        'Media server address'
    ];

    static LEDLight: string[] = ['LEDLight', 'LED light'];
    static ShockReminding: string[] = ['ShockReminding', 'Haptic feedback'];
    static BeepSound: string[] = ['BeepSound', 'Audible tones'];
    static StealthMode: string[] = ['StealthMode', 'Stealth mode'];
    static RecordingNotification: string[] = [
        'RecordingNotification',
        'Recording notification'
    ];
    static LowBatteryWarning: string[] = [
        'LowBatteryWarning',
        'Low battery warning'
    ];
    static LowSpaceWarning: string[] = [
        'LowSpaceWarning',
        'Low storage warning'
    ];
    static KeyTone: string[] = ['KeyTone', 'Key tone'];
    static KeyToneVolume: string[] = ['Volume', 'Key tone volume'];
    static DisplayBrightness: string[] = [
        'Brightness',
        'Display brightness'
    ];
    static LCDAutoOff: string[] = ['ScreenSaver', 'Screen timeout'];
    static IRMode: string[] = ['IRMode', 'IR mode'];
    static IRCUTSensitivity: string[] = ['IRCUTSensitivity', 'IR sensitivity'];
    static LocationService: string[] = ['LocationService', 'GPS'];
    static GPSFrequency: string[] = ['GPSFrequency', 'GPS update frequency'];
    static EnableWatermark: string[] = [
        'EnableWatermark',
        'Display text overlay'
    ];
    static BurnText: string[] = ['BurnText', 'Text overlay'];
    static PowerOff: string[] = ['PowerOff', 'Auto power off'];
    static OutputTV: string[] = ['OutputTV', 'Video standard'];
    static EncodingFormat: string[] = ['EncodingFormat', 'Video encoding'];
    static RecordingSize: string[] = ['RecordingSize', 'Recording resolution'];
    static RecordingTime: string[] = [
        'RecordingTime',
        'Recording segmentation'
    ];
    static RecordingQuality: string[] = ['RecordingQuality', 'Recording quality'];
    static StreamingResolution: string[] = [
        'StreamingResolution',
        'Streaming resolution'
    ];
    static StreamingFrameRate: string[] = [
        'StreamingFrameRate',
        'Streaming frame rate'
    ];
    static StreamingBitrate: string[] = [
        'StreamingBitrate',
        'Streaming bit rate'
    ];
    static StreamingControl: string[] = [
        'StreamingControl',
        'Streaming control'
    ];
    static PreRecordingTime: string[] = ['PreRecordingTime', 'Pre record'];
    static PostRecord: string[] = ['PostRecord', 'Post record'];
    static VideoMute: string[] = ['VideoMute', 'Mute recording'];
    static VideoEIS: string[] = ['VideoEIS', 'Video EIS'];
    static DoubleClickRecord: string[] = [
        'DoubleClickRecord',
        'Double click record'
    ];
    static DashCamMode: string[] = ['CarMode', 'Dash camera mode'];
    static AutoRecording: string[] = ['AutoRecording', 'Auto recording'];
    static AutoDetection: string[] = ['AutoDetection', 'Motion detection'];
    static LoopRecording: string[] = ['LoopRecording', 'Loop recording'];
    static PhotoSize: string[] = ['PhotoSize', 'Photo resolution'];
    static SelfieTime: string[] = ['SelfieTime', 'Photo timer'];
    static BurstMode: string[] = ['BurstMode', 'Burst mode'];
    static BluetoothMode: string[] = ['BluetoothMode', 'Bluetooth'];
    static WifiDirectPin: string[] = ["P2PPinCode", 'Wi-Fi direct PIN'];
}

export const WifiModePropertyNames = Object.values(PropertyNames)
    .filter((x) =>
        x[0] === PropertyNames.WifiConnectionMode[0] ||
        x[0] === PropertyNames.WIFI_ConnectTimeout[0] ||
        x[0] === PropertyNames.Ssid[0] ||
        x[0] === PropertyNames.APPassword[0] ||
        x[0] === PropertyNames.STPassword[0] ||
        x[0] === PropertyNames.APCameraIP[0] ||
        x[0] === PropertyNames.STA_AutoGetIP[0] ||
        x[0] === PropertyNames.UpdateCertificate[0] ||
        x[0] === PropertyNames.WifiDirectPin[0]
    )
    .map((val: string) => val[0]);

export const tabs: TabWithIcon[] = [
    { id: TabEnum.Connectivity, icon: faDiagramProject, title: 'Connectivity' },
    { id: TabEnum.Camera, icon: faCamera, title: 'Camera' },
    { id: TabEnum.Video, icon: faVideoCamera, title: 'Video' },
    { id: TabEnum.Photo, icon: faImage, title: 'Photo' }
];

export const cameraSettingGroups: Group[] = [
    {
        tabId: TabEnum.Connectivity,
        id: GroupEnum.Wi_Fi_Global_Settings,
        title: 'Wi-Fi global settings'
    },
    {
        tabId: TabEnum.Connectivity,
        id: GroupEnum.Network_Configuration,
        title: 'Network configuration'
    },
    { tabId: TabEnum.Connectivity, id: GroupEnum.Bluetooth, title: 'Bluetooth' },
    { tabId: TabEnum.Connectivity, id: GroupEnum.Four_G, title: '4G ' },
    {
        tabId: TabEnum.Connectivity,
        id: GroupEnum.Other_Settings,
        title: 'Other settings'
    },
    {
        tabId: TabEnum.Camera,
        id: GroupEnum.Notifications,
        title: 'Notifications'
    },
    {
        tabId: TabEnum.Camera,
        id: GroupEnum.Screen_and_Keys,
        title: 'Screen & keys'
    },
    {
        tabId: TabEnum.Camera,
        id: GroupEnum.Low_light_recording,
        title: 'Low-light recording'
    },
    { tabId: TabEnum.Camera, id: GroupEnum.GPS, title: 'GPS' },
    { tabId: TabEnum.Camera, id: GroupEnum.Text_Overlay, title: 'Text overlay' },
    { tabId: TabEnum.Video, id: GroupEnum.Video_Format, title: 'Video format' },
    {
        tabId: TabEnum.Video,
        id: GroupEnum.Recording_Settings,
        title: 'Recording settings'
    },
    {
        tabId: TabEnum.Video,
        id: GroupEnum.Streaming_Settings,
        title: 'Streaming settings'
    },
    {
        tabId: TabEnum.Video,
        id: GroupEnum.Pre_and_Post_Recording,
        title: 'Pre and post recording'
    },
    {
        tabId: TabEnum.Video,
        id: GroupEnum.Other_Settings_2,
        title: 'Other settings'
    },
    { tabId: TabEnum.Photo, id: GroupEnum.Image_Quality, title: 'Image quality' },
    { tabId: TabEnum.Photo, id: GroupEnum.Image_Capture, title: 'Image capture' }
];

export const cameraList: Camera[] = [
    { id: CameraTypes.Matrix, name: 'Matrix' },
    { id: CameraTypes.Matrix2, name: 'Matrix 2' },
    { id: CameraTypes.Matrix2Plus, name: 'Matrix 2 Plus' },
    { id: CameraTypes.Matrix4G, name: 'Matrix 4G' }
];


export interface Organization {
    CompanyId: number;
    Name: string;
}
export interface Role {
    ID: number;
    RoleName: string;
}

export interface User {
    UserId: number;
    UserName: string;
}

export interface ExternalCamera {
    Key: number;
    Value: string;
}

export interface DeviceSerialNumber {
    Key: number;
    Value: string;
}
